import copy from "copy-text-to-clipboard";
import { FunctionComponent, useCallback, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Option } from "space-monad";
import styled from "styled-components";
import { Button } from "../../../components/button";
import { Icon } from "../../../components/icons";
import { ellipsis, hiddenDesktop } from "../../../components/layout";
import { Loader } from "../../../components/loader";
import { GlobalOverlayContext } from "../../../components/overlay/context";
import { BooleanField, Table } from "../../../components/table";
import { TableHeaders } from "../../../components/table/Head";
import { Widget } from "../../../components/widget";
import { Pageable } from "../../../services/pageable";
import { AppStore } from "../../../store";
import { useFetchable } from "../../../store/common/fetchable";
import { validationFields } from "../../../store/common/models/fields";
import { NotificationActions } from "../../../store/notifications/actions";
import { WalletsActions } from "../../../store/wallets/actions";
import { useBrowserWallet } from "../../../store/wallets/crypto";
import { chainsMap } from "../../../store/wallets/crypto/chain-config";
import { WalletsDTO } from "../../../store/wallets/models/dto";
import { Wallets } from "../../../store/wallets/reducer";
import { colours } from "../../../styles/colours";
import { size } from "../../../styles/media";
import { formatDecimal } from "../../../utils/utils";
import { NoWallets } from "./empty";
import { RemoveWalletModal, RemoveWalletModalProps } from "./remove-modal";
import { Box, Grid } from "@material-ui/core";
import { Fetchable, isFetchableSuccess } from "../../../store/common/fetchable/helpers";
import { AuthDTO } from "../../../store/auth/models/dto";
import { MyAirdropWalletForm } from "./my-airdrop-wallets-form";
import { MyAirdropWalletSelectionForm } from "./my-airdrop-wallets-selection-form";

type SingleWallets = WalletsDTO.Responses.GetSingle.T;
type fieldNames = keyof SingleWallets | "delete";

const StyledWidget = styled(Widget)`
  &:not(:last-child) {
    margin-bottom: 1em;
  }
`;

const TrashIcon = styled(Icon).attrs({ type: "trash" })`
  display: inline-block;
`;

const Address = styled.span`
  @media (max-width: ${size.tablet - 1}px) {
    ${ellipsis};
    max-width: 25vw;
    display: inline-block;
  }
`;

const Balance = styled.div`
  ${hiddenDesktop};
  color: ${colours.darkGrey};
  font-weight: 700;
  ${ellipsis};
  max-width: 25vw;
`;

function useHeaders(): TableHeaders<fieldNames> {
  const address = validationFields.address.useName();
  const blockchain = validationFields.blockchain.useName();
  return useMemo(
    () => [
      {
        name: "address",
        title: address,
      },
      {
        name: "chain",
        title: blockchain,
      },
      {
        name: "balance",
        title: "$SKRT Locked Balance",
        hiddenMobile: true,
      },
      {
        name: "verified",
        title: "Verified",
        centered: true,
      },
      // {
      //   name: "allowAirdrop",
      //   title: "Airdrop",
      //   centered: true,
      // },
      {
        name: "delete",
        title: "",
        centered: true,
      },
    ],
    [address, blockchain]
  );
}

export namespace BrowserWallet {
  export const Address = styled.div`
    color: ${colours.darkGrey};
  `;

  export const Balance = styled.div`
    color: ${colours.blueGrey};
    font-size: 1.4em;
    font-weight: 700;
  `;
}

export const AllWallets = () => {
  const all = useFetchable(
    Wallets.all,
    useMemo<Pageable.T<keyof WalletsDTO.Responses.GetSingle.T>>(
      () => ({
        pagination: {
          size: 20,
          page: 0,
        },
      }),
      []
    )
  );
  const balance = useFetchable(Wallets.totalBalance);

  return (
    <Loader<
      WalletsDTO.Responses.GetAll.T | WalletsDTO.Responses.GetTotalBalance.T
    >
      data={[all, balance]}
    >
      {(data) => {
        const fulfilledAll = data[0] as WalletsDTO.Responses.GetAll.T;
        const fulfilledBalance = data[1] as WalletsDTO.Responses.GetTotalBalance.T;

        return (
          <PresentationalAllWallets
            walletsList={fulfilledAll}
            balance={fulfilledBalance}
          />
        );
      }}
    </Loader>
  );
};

const PresentationalAllWallets: FunctionComponent<{
  walletsList: WalletsDTO.Responses.GetAll.T;
  balance: WalletsDTO.Responses.GetTotalBalance.T;
}> = ({ walletsList, balance }) => {

  const login = useSelector<AppStore, Fetchable<AuthDTO.Responses.Login.T>>(
    (store) => store.auth.login
  );
  const dispatch = useDispatch();
  const {
    balance: browserWalletBalance,
    accounts,
    verify,
    isVerified,
  } = useBrowserWallet();
  const { setOverlay } = useContext(GlobalOverlayContext);
  const headers = useHeaders();

  const data = walletsList.content.map((item) => ({ ...item, delete: null }));

  const pageableState = useSelector<
    AppStore,
    AppStore["wallets"]["wallets"]["pagination"]
  >((store) => store.wallets.wallets.pagination);

  const onPaginationChange = (
    x: Pageable.T<keyof WalletsDTO.Responses.GetSingle.T>
  ) => {
    dispatch(WalletsActions.setPagination(x));
  };

  const onDeleteHandler = useCallback(
    (props: RemoveWalletModalProps) => {
      setOverlay({
        title: "Remove wallet",
        children: <RemoveWalletModal {...props} />,
      });
    },
    [setOverlay]
  );

  return (
    <>
      {Option.all([browserWalletBalance, accounts]).fold(
        () => (<StyledWidget title="Total Staked Balance">
          <BrowserWallet.Balance>{balance} SKRT</BrowserWallet.Balance>
        </StyledWidget>),
        ([b, a]) => (
          <Grid container spacing={2} style={{ paddingBottom: '1em' }}>
            <Grid item xs={12} md={6}>
              <StyledWidget title="Total Staked Balance">
                <BrowserWallet.Balance>{balance} SKRT</BrowserWallet.Balance>
              </StyledWidget>
            </Grid>
            <Grid item xs={12} md={6}>

              <StyledWidget title="Connected Wallet Balance">
                <BrowserWallet.Address>{a.coinbase}</BrowserWallet.Address>
                <BrowserWallet.Balance>
                  {formatDecimal(b.amount)} {b.symbol}
                </BrowserWallet.Balance>
              </StyledWidget>
            </Grid>
          </Grid>
        )
      )}
      {isFetchableSuccess(login) &&
        <StyledWidget >
          <MyAirdropWalletForm userId={login.data.accessToken.credentialsId} /> </StyledWidget>}

      <StyledWidget
        title="Stored Wallets"
        adornment={accounts.fold(
          () => null,
          (a) =>
            isVerified ? null : (
              <Button variant="outlined" colour="danger" onClick={verify}>
                Verify {a.coinbase?.substring(0, 5)}...
                {a.coinbase?.substring(
                  a.coinbase?.length - 3,
                  a.coinbase?.length
                )}
              </Button>
            )
        )}
      >
        {walletsList.pageable.totalElements < 1 ? (
          <NoWallets />
        ) : (
          <Table
            data={data}
            head={headers}
            pagination={walletsList.pageable}
            onPaginationChange={onPaginationChange}
            pageableState={pageableState}
            unsortable={["delete"]}
            renderFields={{
              verified: ({ verified, address, chain }) =>
                !verified &&
                  accounts.exists(
                    ({ coinbase, networkId }) =>
                      coinbase?.toLowerCase() === address.toLowerCase() &&
                      chain === chainsMap[networkId]
                  ) &&
                  !isVerified ? (
                  <Button variant="outlined" colour="danger" onClick={verify}>
                    Verify
                  </Button>
                ) : (
                  <BooleanField
                    colour1={verified ? colours.green : colours.red}
                  />
                ),
              // allowAirdrop: ({ allowAirdrop }) => (
              //   <BooleanField
              //     colour1={allowAirdrop ? colours.green : colours.red}
              //   />
              // ),
              address: ({ address, balance }) => (
                <Address>
                  {address}
                  <Balance>{balance} SKRT</Balance>
                </Address>
              ),
              delete: () => <TrashIcon colour1={colours.redFailed}></TrashIcon>,
            }}
            cellOnClick={{
              delete: onDeleteHandler,
            }}
            rowOnClick={({ address }) => {
              copy(address);
              dispatch(
                NotificationActions.setNotification({
                  variant: "success",
                  title: "address-copied-to-clipboard",
                })
              );
            }}
          />
        )}
      </StyledWidget>

      {isFetchableSuccess(login) &&
        <StyledWidget >
          <MyAirdropWalletSelectionForm userId={login.data.accessToken.credentialsId} /> </StyledWidget>}
    </>
  );
};
